import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaStar, FaStarHalfAlt } from "react-icons/fa";
import styled from "styled-components";
import Navbar from "../Components/Navbar";
import LoginModal from "../Components/LoginModal";
import SignUpModal from "../Components/SignUpModal";
import ProductFeedback from "../Components/ProductFeedback";
import useCart from "../hooks/useCart";
import useFetchProducts from "../hooks/useProducts";
import logo from "../Assets/logo.png";

const getStoredUserId = () => {
  const stored = localStorage.getItem("userId");
  const parsed = Number(stored);
  return stored && !isNaN(parsed) ? parsed : null;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isAddingToCartButton, setIsAddingToCartButton] = useState(false); // New loading state for Add to Cart
  const userId = getStoredUserId();

  const {
    selectedItems,
    setSelectedItems,
    handleAddToCart,
    handleAddToCartForBuyNow,
    popupMessage,
    closePopup,
    showPopup,
    setShowPopup,
    isLoggedIn,
    cartItems,
  } = useCart(userId);

  const { product, products, ratings, loading, error } = useFetchProducts(id);

  const toggleLoginModal = () => setIsLoginModalOpen((prev) => !prev);
  const toggleSignUpModal = () => setIsSignUpModalOpen((prev) => !prev);

  const handleBuyNow = async (product) => {
    setIsAddingToCart(true);
    const success = await handleAddToCartForBuyNow(product);
    if (!isLoggedIn || !success) {
      setIsAddingToCart(false);
      return;
    }
    const addedItem = cartItems.find((item) => item.id === product.id);
    if (!addedItem) {
      setIsAddingToCart(false);
      return;
    }
    const quantity = selectedItems[product.id]?.quantity || 1;
    const selectedProducts = [
      {
        id: product.id,
        name: product.product_name,
        price: product.price,
        quantity: quantity,
        image: product.image,
      },
    ];
    const totalPrice = product.price * quantity;
    setIsAddingToCart(false);
    navigate("/cart", {
      state: {
        selectedProducts,
        totalPrice,
      },
    });
  };

  const handleAddToCartWithLoading = async (product) => {
    setIsAddingToCartButton(true);
    try {
      await handleAddToCart(product);
    } finally {
      setIsAddingToCartButton(false);
    }
  };

  const handleQuantityChange = (productId, action, event = null) => {
    setSelectedItems((prev) => {
      const currentQuantity = prev[productId]?.quantity || 1;
      let newQuantity = currentQuantity;

      if (action === "increment") {
        newQuantity += 1;
      } else if (action === "decrement") {
        newQuantity = Math.max(1, currentQuantity - 1);
      } else if (action === "input" && event) {
        const value = parseInt(event.target.value);
        if (!isNaN(value) && value > 0) {
          newQuantity = value;
        }
      }

      return {
        ...prev,
        [productId]: {
          ...prev[productId],
          quantity: newQuantity,
        },
      };
    });
  };

  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinnerContainer>
          <LoadingLogo src={logo} alt="Loading Logo" />
          <InnerSpinner />
        </LoadingSpinnerContainer>
        <LoadingText>LOADING</LoadingText>
      </LoadingOverlay>
    );
  }

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  const quantity = selectedItems[product.id]?.quantity || 1;
  const totalAmount = quantity * (product.price || 0);

  return (
    <>
      <Navbar activeSection="marketplace" isLoggedIn={isLoggedIn} />

      <div style={styles.mainContainer}>
        <div style={styles.productDisplay}>
          <div style={styles.productCard}>
            <img
              src={
                product.image
                  ? `data:image/jpeg;base64,${product.image}`
                  : "https://via.placeholder.com/400"
              }
              alt={product.product_name || "Unnamed Product"}
              style={styles.mainProductImage}
            />
          </div>

          <div style={styles.productInfo}>
            <h2>{product.product_name || "Unnamed Product"}</h2>
            <p style={styles.price}>
              ₱{product.price}{" "}
              {product.is_kilo ? "per kilo" : product.is_pieces ? "per piece" : "per unit"}
            </p>
            <p><strong>About this Product:</strong></p>
            <p>{product.description || "No description available."}</p>

            <div style={{ marginTop: "8px" }}>
              <p style={{ fontWeight: "bold", marginBottom: "4px" }}>Ratings:</p>
              <div style={{ display: "flex", alignItems: "center" }}>
                {Array.from({ length: 5 }, (_, i) => {
                  const rating = product.ratings || 0;
                  const fullStar = i < Math.floor(rating);
                  const halfStar = !fullStar && i < Math.ceil(rating) && rating % 1 !== 0;

                  return (
                    <div key={i} style={{ display: "inline-block" }}>
                      {fullStar ? (
                        <FaStar color="gold" size={20} />
                      ) : halfStar ? (
                        <FaStarHalfAlt color="gold" size={20} />
                      ) : (
                        <FaStar color="#ccc" size={20} />
                      )}
                    </div>
                  );
                })}
                <span style={{ marginLeft: "8px", fontSize: "18px" }}>
                  {(product.ratings || 0).toFixed(1)}
                </span>
              </div>
            </div>

            <div style={styles.quantityButton}>
              <button
                style={styles.quantityButtonElement}
                onClick={() => handleQuantityChange(product.id, "decrement")}
              >
                -
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => handleQuantityChange(product.id, "input", e)}
                style={styles.quantityInput}
              />
              <button
                style={styles.quantityButtonElement}
                onClick={() => handleQuantityChange(product.id, "increment")}
              >
                +
              </button>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <p><strong>Total Price:</strong> ₱{totalAmount.toFixed(2)}</p>
            </div>

            <div style={styles.buttonsContainer}>
              <button
                style={{
                  ...styles.addToCartButton,
                  ...(isAddingToCartButton ? styles.addToCartButtonDisabled : {}),
                }}
                onClick={() => handleAddToCartWithLoading(product)}
                disabled={isAddingToCartButton}
              >
                {isAddingToCartButton ? (
                  <span style={styles.loadingContainer}>
                    <span style={styles.buttonSpinner}></span>
                    Adding...
                  </span>
                ) : (
                  <>
                    <span style={styles.cartIcon}>🛒</span> Add To Cart
                  </>
                )}
              </button>
              <button
                style={{
                  ...styles.buyNowButton,
                  ...(isAddingToCart ? styles.buyNowButtonDisabled : {}),
                }}
                onClick={() => handleBuyNow(product)}
                disabled={isAddingToCart}
              >
                {isAddingToCart ? (
                  <span style={styles.loadingContainer}>
                    <span style={styles.buttonSpinner}></span>
                    Adding...
                  </span>
                ) : (
                  "Buy Now"
                )}
              </button>
            </div>
          </div>

          <SuggestedProducts
            products={products}
            currentProduct={product}
            navigate={navigate}
            ratings={ratings}
          />
        </div>

        <ProductFeedback productId={id} />

        {products.filter((p) => p.category === product.category && p.id !== product.id).length === 0 && (
          <div style={{ height: "20px", marginTop: "20px" }} />
        )}

        <h3 style={styles.otherProductsHeader}>Other Products</h3>
        <OtherProducts
          products={products}
          currentProduct={product}
          navigate={navigate}
          ratings={ratings}
        />
      </div>

      {showPopup && (
        <div style={styles.overlay}>
          <div style={styles.popup}>
            <p>⚠️ You need to log in before adding items to the cart.</p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                style={{ ...styles.button, backgroundColor: "#008000" }}
                onClick={() => setShowPopup(false)}
              >
                Cancel
              </button>
              {!isLoggedIn && (
                <button
                  style={styles.button}
                  onClick={() => {
                    setShowPopup(false);
                    setIsLoginModalOpen(true);
                  }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {popupMessage && !showPopup && (
        <div style={styles.overlay}>
          <div style={styles.popup}>
            <p>{popupMessage}</p>
            <button style={styles.button} onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}

      {isLoginModalOpen && (
        <LoginModal isOpen={isLoginModalOpen} toggleModal={toggleLoginModal} />
      )}
      {isSignUpModalOpen && (
        <SignUpModal onClose={toggleSignUpModal} />
      )}
    </>
  );
};

const SuggestedProducts = ({ products, currentProduct, navigate, ratings }) => {
  const suggestedProducts = products.filter(
    (p) => p.category === currentProduct.category && p.id !== currentProduct.id
  );

  if (suggestedProducts.length === 0) {
    return null;
  }

  return (
    <div style={styles.suggestedContainer}>
      <h3 style={styles.header}>Suggested Products</h3>
      <div style={styles.suggestedProductsGrid}>
        {suggestedProducts.map((product) => (
          <div
            key={product.id}
            onClick={() => navigate(`/products/${product.id}`)}
            style={styles.suggestedProductCard}
          >
            <img
              src={
                product.image
                  ? `data:image/jpeg;base64,${product.image}`
                  : "https://via.placeholder.com/300"
              }
              alt={product.product_name}
              style={styles.suggestedProductImage}
            />
            <div style={styles.suggestedProductName}>
              {product.product_name}
              <div>₱{product.price}</div>
              <div style={styles.ratingContainer}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {Array.from({ length: 5 }, (_, i) => {
                    const rating = product.ratings || 0;
                    const fullStar = i < Math.floor(rating);
                    const halfStar = !fullStar && i < Math.ceil(rating) && rating % 1 !== 0;

                    return (
                      <div key={i} style={{ display: "inline-block" }}>
                        {fullStar ? (
                          <FaStar color="gold" size={14} />
                        ) : halfStar ? (
                          <FaStarHalfAlt color="gold" size={14} />
                        ) : (
                          <FaStar color="#ccc" size={14} />
                        )}
                      </div>
                    );
                  })}
                  <span style={{ marginLeft: "4px", fontSize: "12px" }}>
                    {(product.ratings || 0).toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OtherProducts = ({ products, currentProduct, navigate, ratings }) => {
  const suggestedProducts = products.filter(
    (p) => p.category === currentProduct.category && p.id !== currentProduct.id
  );

  const suggestedProductIds = suggestedProducts.map((p) => p.id);

  const otherProducts = products.filter(
    (p) => p.id !== currentProduct.id && !suggestedProductIds.includes(p.id)
  );

  if (otherProducts.length === 0) {
    return <p>No other products available.</p>;
  }

  return (
    <div style={styles.productsGrid}>
      {otherProducts.map((product) => (
        <div
          key={product.id}
          onClick={() => navigate(`/products/${product.id}`)}
          style={styles.productCard}
        >
          <img
            src={
              product.image
                ? `data:image/jpeg;base64,${product.image}`
                : "https://via.placeholder.com/300"
            }
            alt={product.product_name}
            style={styles.otherProductImage}
          />
          <div style={styles.productDetails}>
            <h3>{product.product_name}</h3>
            <p>₱{product.price}</p>
            <div style={styles.ratingContainer}>
              <div style={{ display: "flex", alignItems: "center" }}>
                {Array.from({ length: 5 }, (_, i) => {
                  const rating = product.ratings || 0;
                  const fullStar = i < Math.floor(rating);
                  const halfStar = !fullStar && i < Math.ceil(rating) && rating % 1 !== 0;

                  return (
                    <div key={i} style={{ display: "inline-block" }}>
                      {fullStar ? (
                        <FaStar color="gold" size={16} />
                      ) : halfStar ? (
                        <FaStarHalfAlt color="gold" size={16} />
                      ) : (
                        <FaStar color="#ccc" size={16} />
                      )}
                    </div>
                  );
                })}
                <span style={{ marginLeft: "4px", fontSize: "14px" }}>
                  {(product.ratings || 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Styled Components for Loading
const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: linear-gradient(45deg, #4caf50, rgb(30, 103, 34));
  border-radius: 20px;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  margin: 0;
  max-width: 100%;
  z-index: 1000;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
    z-index: 0;
    animation: rotate 8s linear infinite;
  }

  @keyframes rotate {
    100% {
      transform: rotate(1turn);
    }
  }
`;

const LoadingSpinnerContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingLogo = styled.img`
  width: 100px;
  height: 100px;
  position: absolute;
  border-radius: 50%;
  z-index: 2;
`;

const InnerSpinner = styled.div`
  position: absolute;
  width: 120px;
  height: 120px;
  border: 2px solid transparent;
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    top: 5px;
    left: 5px;
    right: 5px;
    bottom: 5px;
    border: 2px solid transparent;
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: spin 1.2s linear infinite reverse;
  }

  &:after {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border: 2px solid transparent;
    border-top-color: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    animation: spin 1.6s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  position: relative;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: 3px;

  &::after {
    content: "...";
    position: absolute;
    animation: loading 1s infinite;

    @keyframes loading {
      0% {
        content: ".";
      }
      33% {
        content: "..";
      }
      66% {
        content: "...";
      }
    }
  }
`;

// Updated styles object
const styles = {
  mainContainer: {
    padding: "20px",
    backgroundColor: "#eaf4e1",
    minHeight: "100vh",
    overflowX: "hidden",
  },
  productDisplay: {
    display: "flex",
    gap: "20px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  mainProductImage: { width: "400px", height: "330px", objectFit: "cover", borderRadius: "10px" },
  productInfo: {
    flex: "0 0 300px",
  },
  buttonsContainer: {
    display: "flex",
    gap: "10px",
    marginTop: "20px",
  },
  addToCartButton: {
    backgroundColor: "#eaf4e1",
    color: "green",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "2px solid green",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  addToCartButtonDisabled: {
    backgroundColor: "#eaf4e1",
    color: "rgba(0, 128, 0, 0.6)",
    border: "2px solid rgba(0, 128, 0, 0.6)",
    cursor: "not-allowed",
  },
  buyNowButton: {
    backgroundColor: "green",
    color: "white",
    padding: "8px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buyNowButtonDisabled: {
    backgroundColor: "rgba(0, 128, 0, 0.6)",
    cursor: "not-allowed",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  buttonSpinner: {
    width: "12px",
    height: "12px",
    border: "2px solid #fff",
    borderTop: "2px solid transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  cartIcon: {
    marginRight: "5px",
    fontSize: "1.2rem",
  },
  quantityButton: { display: "flex", alignItems: "center", gap: "12px" },
  quantityButtonElement: {
    background: "none",
    border: "1px solid #ccc",
    padding: "5px",
    width: "30px",
    height: "30px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  quantityInput: {
    width: "40px",
    height: "30px",
    textAlign: "center",
    fontSize: "20px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    MozAppearance: "textfield",
    WebkitAppearance: "none",
    appearance: "none",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  popup: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    textAlign: "center",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  },
  button: {
    marginTop: "10px",
    padding: "8px 16px",
    backgroundColor: "#008000",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
  productsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    marginTop: "20px",
    padding: "0 10px",
  },
  productCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "15px",
    border: "1px solid #ddd",
    textAlign: "center",
    cursor: "pointer",
    transition: "transform 0.3s ease-in-out",
  },
  otherProductImage: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: "10px",
  },
  productDetails: { padding: "15px" },
  suggestedContainer: {
    marginTop: "0",
    flex: "1",
    minWidth: "300px",
  },
  header: {
    marginTop: "0",
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#004d00",
  },
  suggestedProductsGrid: {
    display: "flex",
    flexDirection: "row",
    gap: "20px",
    marginTop: "10px",
    padding: "0 10px",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },
  suggestedProductCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
    padding: "10px",
    cursor: "pointer",
    transition: "transform 0.3s ease-in-out",
    height: "250px",
    width: "133px",
  },
  suggestedProductImage: {
    width: "100%",
    height: "150px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  suggestedProductName: {
    marginTop: "10px",
    fontWeight: "bold",
    fontSize: "14px",
  },
  ratingContainer: {
    backgroundColor: "#f9f9f9",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-flex",
    alignItems: "center",
    marginTop: "4px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  otherProductsHeader: {
    marginTop: "5px",
    fontSize: "1.5rem",
    fontWeight: "bold",
    color: "#004d00",
    textAlign: "left",
    maxWidth: "fit-content",
    width: "auto",
    marginLeft: "1.5rem",
    marginRight: "0",
  },
};

export default ProductDetail;